import {Box} from "@mui/material";
import {formatDateHM, formatDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {ElementChip} from "../../common/uiElements/base/ElementChip";
import {StatusDot} from "../../common/uiElements/base/StatusDot";
import {FolioFormat} from "../../system/billing/folio/editor/folioFormat";
import {DigitalSigningStatusView} from "../digitalSigning.model";
import {useNow} from "./digitalSigningFlow.hooks";
import {countdownText, remainingMs} from "./digitalSigningFlow.util";
import {DigitalSigningFlow} from "./useDigitalSigning";

interface StatusChipProps {
    text: string;
    color: string;
    tooltip?: string;
    onClick: () => void;
}

const StatusChip = ({text, color, tooltip, onClick}: StatusChipProps) => (
    <Box
        component={'span'}
        sx={{display: 'inline-flex', flexShrink: 0, maxWidth: '100%', '& [data-element-chip-label]': {fontVariantNumeric: 'tabular-nums'}}}
    >
        <ElementChip text={text} startIcon={<StatusDot color={color}/>} tooltipContent={tooltip} onClick={onClick}/>
    </Box>
);

interface LiveProps {
    signing: DigitalSigningStatusView;
    onClick: () => void;
    onExpired: () => void;
}

const LiveChip = ({signing, onClick, onExpired}: LiveProps) => {
    const {t} = useTranslation();
    const now = useNow();
    const remaining = remainingMs(signing.expiresAt, now);
    const expired = remaining !== undefined && remaining <= 0;

    useEffect(() => {
        if (expired) onExpired();
    }, [expired]);

    return (
        <StatusChip
            text={remaining === undefined
                ? t('Waiting for the guest')
                : t('Waiting for the guest · {{time}}', {time: countdownText(remaining)})}
            color={'info.main'}
            tooltip={t('Link issued at {{time}}', {time: formatDateHM(signing.issuedAt)})}
            onClick={onClick}
        />
    );
};

interface Props {
    flow: DigitalSigningFlow;
    format: FolioFormat;
}

const DigitalSigningChip = ({flow, format}: Props) => {
    const {t} = useTranslation();
    const {state, latest, confirmed} = flow;

    if (confirmed) {
        return (
            <StatusChip
                text={t('Confirmed')}
                color={'success.main'}
                tooltip={t('Confirmed on {{date}}', {date: formatDateTime('dd.MM.yyyy HH:mm', confirmed.confirmedAt)})}
                onClick={flow.showDetails}
            />
        );
    }
    if (!latest) return null;

    switch (state.kind) {
        case 'LIVE':
            return <LiveChip key={latest.id} signing={latest} onClick={flow.openDialog} onExpired={() => void flow.refetch()}/>;
        case 'LIVE_WITHOUT_TOKEN':
            return (
                <StatusChip
                    text={t('Link open')}
                    color={'info.main'}
                    tooltip={t('Link issued at {{time}}', {time: formatDateHM(latest.issuedAt)})}
                    onClick={flow.openDialog}
                />
            );
        case 'SIGNED': {
            if (flow.stale) {
                return (
                    <StatusChip
                        text={t('Signature outdated')}
                        color={'warning.main'}
                        tooltip={t('The folio changed after the guest signed. Please create a new link.')}
                        onClick={flow.openDialog}
                    />
                );
            }
            const tipCents = latest.tip?.amountCents ?? 0;
            return (
                <StatusChip
                    text={tipCents > 0 ? t('Signed · tip {{amount}}', {amount: format.money(tipCents)}) : t('Signed · no tip')}
                    color={'success.main'}
                    tooltip={t('Signed at {{time}}', {time: formatDateHM(latest.signedAt)})}
                    onClick={flow.showDetails}
                />
            );
        }
        default:
            return null;
    }
};

export default DigitalSigningChip;
